import ErrorBoundary from "@/components/ErrorBoundary";
import { FloatingContact } from "@/components/FloatingContact";
import { PublicFooter } from "@/components/PublicFooter";
import { PublicHeader } from "@/components/PublicHeader";
import { RouteScrollReset } from "@/components/RouteScrollReset";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Article from "@/pages/Article";
import Contact from "@/pages/Contact";
import Discovery from "@/pages/Discovery";
import FaqPage from "@/pages/FaqPage";
import Guide from "@/pages/Guide";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Moving from "@/pages/Moving";
import { InfoPage } from "@/pages/InfoPage";
import LegalPage from "@/pages/LegalPage";
import NotFound from "@/pages/NotFound";
import Packages from "@/pages/Packages";
import PrivateInvestments from "@/pages/PrivateInvestments";
import PrivatePortal from "@/pages/PrivatePortal";
import SetupCheck from "@/pages/SetupCheck";
import Stay from "@/pages/Stay";
import StayDetail from "@/pages/StayDetail";
import Testimonials from "@/pages/Testimonials";
import { lazy, ReactNode, Suspense, useEffect, useState } from "react";
import { Route, Switch } from "wouter";

const Admin = lazy(() => import("@/pages/Admin"));
const CookieConsent = lazy(() => import("@/components/CookieConsent").then(module => ({ default: module.CookieConsent })));

function PublicLayout({ children }: { children: ReactNode }) {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => { setHydrated(true); }, []);
  return <div className="min-h-screen bg-[#F7F3EB] text-[#1B1B1B]"><RouteScrollReset /><PublicHeader />{children}<PublicFooter /><FloatingContact />{hydrated && <Suspense fallback={null}><CookieConsent /></Suspense>}</div>;
}
function PublicRoute({ children }: { children: ReactNode }) { return <PublicLayout>{children}</PublicLayout>; }
function AdminRoute({ section }: { section: Parameters<typeof Admin>[0]["section"] }) { return <Suspense fallback={<div className="min-h-screen bg-[#101010]" />}><Admin section={section} /></Suspense>; }

function Router() {
  return <Switch>
    <Route path="/admin"><AdminRoute section="dashboard" /></Route>
    <Route path="/admin/leads"><AdminRoute section="leads" /></Route>
    <Route path="/admin/pakete"><AdminRoute section="packages" /></Route>
    <Route path="/admin/stay"><AdminRoute section="stay" /></Route>
    <Route path="/admin/ratgeber"><AdminRoute section="posts" /></Route>
    <Route path="/admin/private"><AdminRoute section="private" /></Route>
    <Route path="/admin/faq"><AdminRoute section="faq" /></Route>
    <Route path="/admin/inhalte"><AdminRoute section="content" /></Route>
    <Route path="/admin/benutzer"><AdminRoute section="users" /></Route>
    <Route path="/"><PublicRoute><Home /></PublicRoute></Route>
    <Route path="/leistungen"><PublicRoute><Services /></PublicRoute></Route>
    <Route path="/firmengruendung-nordzypern"><PublicRoute><InfoPage pageKey="company" /></PublicRoute></Route>
    <Route path="/konto-eroeffnen-nordzypern"><PublicRoute><InfoPage pageKey="banking" /></PublicRoute></Route>
    <Route path="/geschaeftskonto-nordzypern"><PublicRoute><InfoPage pageKey="businessAccount" /></PublicRoute></Route>
    <Route path="/pakete"><PublicRoute><Packages /></PublicRoute></Route>
    <Route path="/business-nordzypern"><PublicRoute><InfoPage pageKey="business" /></PublicRoute></Route>
    <Route path="/nordzypern"><PublicRoute><InfoPage pageKey="northCyprus" /></PublicRoute></Route>
    <Route path="/nordzypern-erleben"><PublicRoute><Discovery /></PublicRoute></Route>
    <Route path="/auswandern-nordzypern"><PublicRoute><Moving /></PublicRoute></Route>
    <Route path="/leben-nordzypern"><PublicRoute><InfoPage pageKey="living" /></PublicRoute></Route>
    <Route path="/investieren-nordzypern"><PublicRoute><InfoPage pageKey="invest" /></PublicRoute></Route>
    <Route path="/immobilien-nordzypern"><PublicRoute><InfoPage pageKey="realEstate" /></PublicRoute></Route>
    <Route path="/ferienwohnung-nordzypern"><PublicRoute><InfoPage pageKey="holidayRental" /></PublicRoute></Route>
    <Route path="/private-investments"><PublicRoute><PrivateInvestments /></PublicRoute></Route>
    <Route path="/ueber-uns"><PublicRoute><InfoPage pageKey="about" /></PublicRoute></Route>
    <Route path="/kundenstimmen"><PublicRoute><Testimonials /></PublicRoute></Route>
    <Route path="/ratgeber"><PublicRoute><Guide /></PublicRoute></Route>
    <Route path="/ratgeber/:slug"><PublicRoute><Article /></PublicRoute></Route>
    <Route path="/faq"><PublicRoute><FaqPage /></PublicRoute></Route>
    <Route path="/kontakt"><PublicRoute><Contact /></PublicRoute></Route>
    <Route path="/setup-check"><PublicRoute><SetupCheck /></PublicRoute></Route>
    <Route path="/stay"><PublicRoute><Stay /></PublicRoute></Route>
    <Route path="/stay/unterkunft/:slug"><PublicRoute><StayDetail /></PublicRoute></Route>
    <Route path="/private"><PublicRoute><PrivatePortal /></PublicRoute></Route>
    <Route path="/impressum"><PublicRoute><LegalPage type="imprint" /></PublicRoute></Route>
    <Route path="/datenschutz"><PublicRoute><LegalPage type="privacy" /></PublicRoute></Route>
    <Route path="/rechtliche-hinweise"><PublicRoute><LegalPage type="legal" /></PublicRoute></Route>
    <Route path="/nordzypern/:region"><PublicRoute><InfoPage pageKey="northCyprus" /></PublicRoute></Route>
    <Route path="/404"><PublicRoute><NotFound /></PublicRoute></Route>
    <Route><PublicRoute><NotFound /></PublicRoute></Route>
  </Switch>;
}

export default function App() {
  return <ErrorBoundary><LanguageProvider><ThemeProvider defaultTheme="light"><TooltipProvider><Toaster richColors /><Router /></TooltipProvider></ThemeProvider></LanguageProvider></ErrorBoundary>;
}
